import { Box } from "@mui/material";
import React, { ReactNode } from "react";
import { useNativeWebViewInsets } from "../context/NativeWebViewInsetsContext";
import { StaggerGroup } from "./animation";
import { heroWaveMaskStyles } from "./HeroWaveMask";

export interface HeroSectionProps {
    /** Background image URL. When omitted, only children are rendered (no image layer). */
    imageUrl?: string | null;
    /** Hero height (any valid CSS length, e.g. "80vh", "55vh", "clamp(296px, 24vh, 240px)"). */
    minHeight?: string;
    /** Whether to apply the bottom wave mask. Default true. */
    showWave?: boolean;
    /** Stagger mount-time entrance animations on direct Reveal children. */
    animateHero?: boolean;
    /** Content overlain on the hero (e.g. title, tagline). */
    children: ReactNode;
}

const heroOverlayStyles = {
    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: `linear-gradient(to right, rgba(0,0,0,1) 10%, rgba(0,0,0,0.5) 50%, transparent 70%)`,
    },
} as const;
/**
 * Full-width hero: covers `minHeight` with an image and optional single-wave mask at the bottom.
 */
const HeroSection: React.FC<HeroSectionProps> = ({
    imageUrl,
    minHeight = "80vh",
    showWave = true,
    animateHero = false,
    children,
}) => {
    const nativeInsets = useNativeWebViewInsets();

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                minHeight,
                display: "flex",
                alignItems: "center",
            }}
        >
            {imageUrl ? (
                <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center center",
                        pointerEvents: "none",
                        ...(showWave ? heroWaveMaskStyles : {}),
                    }}
                />
            ) : null}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    ...(nativeInsets.isNativeWebView && nativeInsets.paddingTop > 0
                        ? { paddingTop: "var(--native-padding-top)" }
                        : {}),
                }}
            >
                {animateHero ? (
                    <StaggerGroup stagger={0.12}>{children}</StaggerGroup>
                ) : (
                    children
                )}
            </Box>

            <Box sx={{
                ...heroOverlayStyles,
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center center",
                ...(showWave ? heroWaveMaskStyles : {}),
            }} className="hero-overlay" />
        </Box>
    );
};

export default HeroSection;
